import { useRestApiProvider } from "@jugl-web/rest-api";
import { DRAWER_TRANSITION_DURATION_MS } from "@jugl-web/ui-kit";
import { useClipboard, useToast, useTranslations } from "@jugl-web/utils";
import { environment } from "@web-src/environments/environment";
import { useManageAccountsDrawer } from "@web-src/modules/client-support/pages/ClientSupportPage/components/ManageAccountsDrawer/providers/ManageAccountsDrawerContext";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { FC, useCallback } from "react";
import { ChannelTile } from "../ChannelTile";
import { ReactComponent as LiveChatIcon } from "./assets/live-chat.svg";

interface LiveChatTileProps {
  isSelected: boolean;
  onSelect: () => void;
}

export const LiveChatTile: FC<LiveChatTileProps> = (props) => {
  const { entity, entityId } = useEntitySelectedProvider();
  const { integrationsApi } = useRestApiProvider();
  const { copyToClipboard } = useClipboard();

  const { crmConfig, openManageAccountDrawer } = useManageAccountsDrawer();

  const [updateLiveChatAppConfig, { isLoading }] =
    integrationsApi.useUpdateLiveChatAppConfigMutation();

  const { t } = useTranslations();
  const { toast } = useToast();

  const isEnabled = crmConfig?.livechat?.enabled === true;

  const handleEnable = useCallback(async () => {
    if (isLoading) {
      return;
    }

    const response = await updateLiveChatAppConfig({ entityId, enabled: true });

    if (response && "data" in response) {
      toast(
        t({
          id: "cx-ai-agents-page.live-chat-has-been-enabled",
          defaultMessage: "Live chat has been enabled",
        })
      );
      return;
    }

    toast(
      t({
        id: "feedback.something-went-wrong",
        defaultMessage: "Something went wrong",
      }),
      { variant: "error" }
    );
  }, [entityId, isLoading, t, toast, updateLiveChatAppConfig]);

  return (
    <ChannelTile
      title={t({
        id: "cx-ai-agents-page.live-chat-via-link",
        defaultMessage: "Live Chat via Link",
      })}
      description={t({
        id: "cx-ai-agents-page.live-chat-via-link-description",
        defaultMessage: "Send a link and start chatting",
      })}
      icon={<LiveChatIcon />}
      isConnected={isEnabled}
      onClick={() => {
        openManageAccountDrawer({ name: "liveChatAppAccount" });

        if (!isEnabled) {
          setTimeout(handleEnable, DRAWER_TRANSITION_DURATION_MS + 50);
        }
      }}
      pillText={
        isEnabled
          ? t({
              id: "common.enabled",
              defaultMessage: "Enabled",
            })
          : undefined
      }
      onCopy={() => {
        copyToClipboard(`${environment.liveChatUrl}/${entity.uniqueId}`);
      }}
      onCopyHint={t({
        id: "cx-ai-agents-page.live-chat-via-link-copy-hint",
        defaultMessage: "Copy Live Chat link",
      })}
      {...props}
    />
  );
};
